import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import breakpoints from "assets/theme/base/breakpoints";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { useEffect, useState } from "react";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import PlacePicker from "components/MDPlacePicker/PlacePicker";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import CancelIcon from "@mui/icons-material/Cancel";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import DensitySmallIcon from "@mui/icons-material/DensitySmall";
import PriceCheckIcon from "@mui/icons-material/PriceCheck";
import PendingIcon from "@mui/icons-material/Pending";
import { FormControl } from "@mui/material";
import TransactionSearchBar from "./components/TransactionSearchBar";
import TransactionFilterByDate from "./components/TransactionFilterByDate";
import TransactionsTable from "./components/TransactionTable";
import CustomTooltip from "components/Common/CustomTooltip";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && <MDBox sx={{ p: 3 }}>{children}</MDBox>}
    </div>
  );
}

function TabLabelWithInfo({ label, tooltip }) {
  return (
    <MDBox display="flex" alignItems="center" gap={0.5}>
      <span>{label}</span>
      <Tooltip title={tooltip} arrow placement="top">
        <InfoOutlinedIcon
          sx={{
            fontSize: 16,
            color: "text.secondary",
            cursor: "pointer",
          }}
        />
      </Tooltip>
    </MDBox>
  );
}

const tabInfo = {
  all: "View all transactions regardless of status.",
  success: "Transactions that were completed successfully.",
  pendingPayment: "Transactions awaiting payment confirmation.",
  refunded: "Transactions that have been refunded to the customer.",
  failed: "Transactions that failed or were declined.",
};

function Transactions() {
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;
  const location = useLocation();
  const navigate = useNavigate();
  const [tabsOrientation, setTabsOrientation] = useState("horizontal");
  const queryParams = new URLSearchParams(location.search);
  const activeTab = queryParams.get("tab");
  const [tabValue, setTabValue] = useState(activeTab ? activeTab : "all");
  const [openTooltipId, setOpenTooltipId] = useState(null);

  const selectedPlace = useSelector((state) => state.places?.selectedPlace);
  let placeId = selectedPlace?._id;
  const tz = selectedPlace?.timeZoneId || "America/Los_Angeles";

  if (!placeId) {
    placeId = localStorage.getItem("placeId");
  }

  const [filterOptions, setFilterOptions] = useState({
    tz,
    pageNo: 0,
    status: "all",
    placeId,
    startDate: "",
    endDate: "",
    search: "",
  });

  useEffect(() => {
    function handleTabsOrientation() {
      return window.innerWidth < breakpoints.values.sm
        ? setTabsOrientation("vertical")
        : setTabsOrientation("horizontal");
    }

    window.addEventListener("resize", handleTabsOrientation);
    handleTabsOrientation();
    return () => window.removeEventListener("resize", handleTabsOrientation);
  }, [tabsOrientation]);

  const handleSetTabValue = (event, newValue) => {
    setTabValue(newValue);
    queryParams.set("tab", newValue);
    navigate(`?${queryParams.toString()}`);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={2} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={1}
                mt={-2}
                py={1}
                px={1}
                variant="gradient"
                bgColor={sidenavColor}
                borderRadius="lg"
                coloredShadow={sidenavColor}
                className="d-flex align-items-center gap-2"
              >
                <MDTypography variant="h6" color="white">
                  Transactions
                </MDTypography>
              </MDBox>

              <MDBox p={2}>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={4}>
                    <PlacePicker />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <FormControl fullWidth>
                      <TransactionFilterByDate
                        filterOptions={filterOptions}
                        setFilterOptions={setFilterOptions}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                      <TransactionSearchBar
                        filterOptions={filterOptions}
                        setFilterOptions={setFilterOptions}
                      />
                    </FormControl>
                  </Grid>
                </Grid>
              </MDBox>

              <MDBox p={2}>
                <AppBar position="static">
                  <Tabs
                    orientation={tabsOrientation}
                    value={tabValue}
                    onChange={handleSetTabValue}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    <Tab
                      value="all"
                      icon={<DensitySmallIcon fontSize="small" />}
                      label={
                        <MDBox display="flex" alignItems="center">
                          All
                          <CustomTooltip
                            id="all"
                            title={tabInfo.all}
                            openTooltipId={openTooltipId}
                            setOpenTooltipId={setOpenTooltipId}
                            iconVariant="outlined"
                            iconSize={16}
                          />
                        </MDBox>
                      }
                    />
                    <Tab
                      value="success"
                      icon={<PriceCheckIcon fontSize="small" />}
                      label={
                        <MDBox display="flex" alignItems="center">
                          Success
                          <CustomTooltip
                            id="success"
                            title={tabInfo.success}
                            openTooltipId={openTooltipId}
                            setOpenTooltipId={setOpenTooltipId}
                            iconVariant="outlined"
                            iconSize={16}
                          />
                        </MDBox>
                      }
                    />
                    <Tab
                      value="pendingPayment"
                      icon={<PendingIcon fontSize="small" />}
                      label={
                        <MDBox display="flex" alignItems="center">
                          Pending Payment
                          <CustomTooltip
                            id="pendingPayment"
                            title={tabInfo.pendingPayment}
                            openTooltipId={openTooltipId}
                            setOpenTooltipId={setOpenTooltipId}
                            iconVariant="outlined"
                            iconSize={16}
                          />
                        </MDBox>
                      }
                    />
                    <Tab
                      value="refunded"
                      icon={<CurrencyExchangeIcon fontSize="small" />}
                      label={
                        <MDBox display="flex" alignItems="center">
                          Refunded
                          <CustomTooltip
                            id="refunded"
                            title={tabInfo.refunded}
                            openTooltipId={openTooltipId}
                            setOpenTooltipId={setOpenTooltipId}
                            iconVariant="outlined"
                            iconSize={16}
                          />
                        </MDBox>
                      }
                    />
                    <Tab
                      value="failed"
                      icon={<CancelIcon fontSize="small" />}
                      label={
                        <MDBox display="flex" alignItems="center">
                          Failed
                          <CustomTooltip
                            id="failed"
                            title={tabInfo.failed}
                            openTooltipId={openTooltipId}
                            setOpenTooltipId={setOpenTooltipId}
                            iconVariant="outlined"
                            iconSize={16}
                          />
                        </MDBox>
                      }
                    />
                  </Tabs>
                </AppBar>

                {["all", "success", "pendingPayment", "refunded", "failed"].map(
                  (key) => (
                    <TabPanel key={key} value={tabValue} index={key}>
                      <TransactionsTable
                        placeId={placeId}
                        startDate={filterOptions.startDate}
                        endDate={filterOptions.endDate}
                        searchText={filterOptions.search}
                        activeTab={tabValue}
                        tz={tz}
                      />
                    </TabPanel>
                  )
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Transactions;
